/** The fields this decision reads. Kept loose so it can be called with a raw API row. */
export interface PartyPaymentCandidate {
  payeeType?: string | null
  sourceType?: string | null
}

/**
 * Whether a payable belongs on the loan's Party Payments screen.
 *
 * Two things do not.
 *
 * Anything converted from the previous servicer is history rather than work in progress; it stays
 * on the payee's own file under Accounting.
 *
 * Anything owed to a vendor that this loan did not raise by hand is a check to cut, not a party
 * payment. A charge splitting 100% to a vendor queues a payable automatically on every loan, so
 * listing those here asks a servicer to act on something the check queue already handles. That is
 * how Longhorn's Account Maintenance Fee kept reappearing.
 *
 * A vendor payment a servicer raised ON this loan is different, and used to be swept up by the same
 * rule. The inspection fee on a construction draw is the everyday case: it is owed to the inspector,
 * so the payee is a vendor, but it was entered here against this draw and belongs beside it. Hiding
 * it made a correctly raised $150 fee look like it had not saved.
 *
 * A draw paid out from the Construction Reserve tab is the same thing: raised by hand against one
 * ledger row, and usually to the contractor's vendor record. It reads as a party payment even
 * though the payee is a vendor, so 'draw' counts as hand-raised alongside 'manual'.
 *
 * So does 'reissue', the copy raised when the bank returns a payment. It stands in for whichever
 * payment failed, and hiding it would make a returned vendor payment look as though nothing had
 * been done about it - the returned row would be on the screen and its replacement would not.
 *
 * Both kinds are still paid either way; the check queue and the vendor file read them by their own
 * paths. This only decides what the loan's own screen shows.
 */
export function belongsOnPartyPayments(item: PartyPaymentCandidate): boolean {
  const sourceType = String(item?.sourceType || '').toLowerCase()
  if (sourceType === 'tmo') return false

  const isVendor = String(item?.payeeType || '').toLowerCase() === 'vendor'
  // 'manual' is the default the server stamps on anything raised from this screen; 'draw' is a
  // ledger row someone paid out from the Construction Reserve tab; 'drawfee' is one of the fees on
  // that same row, paid to the inspector or the title company; 'reissue' replaces a payment the
  // bank returned.
  return (
    !isVendor ||
    sourceType === 'manual' ||
    sourceType === 'draw' ||
    sourceType === 'drawfee' ||
    sourceType === 'reissue'
  )
}
